import { fetchWithRetry, rejectAfter, resolveAfter } from '@tunein/web-utils';
import asyncRetry from 'async-retry';
import { clamp, isPlainObject, merge, noop, pick } from 'es-toolkit';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { AdsWizzProvider, GoogleIMAProvider } from '../../ads';
import { BaseEngine, DEFAULT_MEDIA_ELEMENT_ID, HLSEngine, HLSLiveSeekEngine, IcyEngine, } from '../../engine';
import { ErrorCode, ErrorName, ErrorType, PlayerError } from '../../errors';
import { debug, setDebugMode } from '../../utils/logger';
import parsePlaylist from '../../utils/parsePlaylist';
import PlayerSettingsStorage from './storage';
const WAITING_RECOVERY_MS = 5000;
const DEFAULT_TIMEOUT_MS = 15_000;
const DEFAULT_VOLUME = 0.5;
export const DEFAULT_OPTIONS = {
    mediaElementId: DEFAULT_MEDIA_ELEMENT_ID,
    onlyUseCoreEngine: false,
    loadTimeoutMs: DEFAULT_TIMEOUT_MS,
    storage: {
        enabled: true,
    },
};
const adProviderMap = {
    google: GoogleIMAProvider,
    adswizz: AdsWizzProvider,
};
var PlayerState;
(function (PlayerState) {
    PlayerState["PAUSED"] = "paused";
    PlayerState["PLAYING"] = "playing";
    PlayerState["IDLE"] = "idle";
    PlayerState["LOADING"] = "loading";
    PlayerState["LOADED"] = "loaded";
    PlayerState["FAILED"] = "failed";
})(PlayerState || (PlayerState = {}));
export default class WebMediaPlayer extends EventEmitter {
    #initOptions;
    #options = { ...DEFAULT_OPTIONS };
    #settingsStorage;
    #engine;
    #previousEngineName;
    #engineEvents;
    #adProviderEvents;
    #ads;
    #volume = DEFAULT_VOLUME;
    #state = PlayerState.IDLE;
    #ssaiManager;
    #currentSourceIndex = 0;
    #src = [];
    #wasPlaybackAttempted = false;
    #wasPlaybackStarted = false;
    #cachedPlaybackRate = null;
    #loadId = null;
    #isAdBreak = false;
    #retryOnceWithCoreEngine = false;
    #retryOnceOnError = false;
    #isRetrying = false;
    #cancelRetry = null;
    #isUserInitiatedPause = false;
    constructor(options) {
        super();
        this.#buildEventMaps();
        this.#initOptions = options;
    }
    get currentSource() {
        return this.#src[this.#currentSourceIndex];
    }
    get adMediaElement() {
        const adMediaElement = this.#ads?.mediaElement;
        if (adMediaElement) {
            return adMediaElement;
        }
        const mediaElementId = this.#options.advertising?.providerOptions?.mediaElementId;
        if (!mediaElementId) {
            return undefined;
        }
        return globalThis.document?.getElementById(mediaElementId);
    }
    get adContainerElement() {
        return this.#ads?.containerElement;
    }
    get muted() {
        if (this.#ads) {
            return !this.#ads.volume;
        }
        return this.#engine?.muted || false;
    }
    getEngineName() {
        return this.#engine?.name || this.#previousEngineName;
    }
    setOptions(options = {}, deeplyMergeOptions = true) {
        setDebugMode(options.debug);
        if (deeplyMergeOptions) {
            this.#options = [
                DEFAULT_OPTIONS,
                this.#options,
                options,
            ].reduce(merge, {});
        }
        else {
            this.#options = {
                ...DEFAULT_OPTIONS,
                ...this.#options,
                ...options,
            };
        }
        if (options.timeoutMs === 0) {
            this.#options.loadTimeoutMs = 0;
        }
        if (options.timeoutMs) {
            if (options.timeoutMs < WAITING_RECOVERY_MS) {
                debug(`Minimum options.timeoutMs threshold not met. Resetting to ${WAITING_RECOVERY_MS * 1000}.`);
            }
            this.#options.loadTimeoutMs = Math.max(options.timeoutMs, WAITING_RECOVERY_MS);
        }
        this.#initAdsProvider();
        if (options.src) {
            this.#setSources(options.src);
        }
        if (options.volume !== undefined) {
            this.setVolume(options.volume);
        }
    }
    getOptions() {
        return this.#options;
    }
    init() {
        if (this.#initOptions) {
            this.setOptions(this.#initOptions);
        }
        if (this.#options.storage.enabled) {
            this.#settingsStorage = new PlayerSettingsStorage();
            this.#setSettingsFromStorage();
        }
        if (this.#options.onlyUseCoreEngine) {
            this.#setupEngine();
        }
    }
    playBlankMp3(adProviderOptions, { skipEngine = false } = {}) {
        const adPrime = adProviderOptions
            ? new GoogleIMAProvider(adProviderOptions).playBlankMp3()
            : undefined;
        if (skipEngine) {
            return adPrime ?? Promise.resolve(false);
        }
        const enginePrime = new BaseEngine(this.#buildEngineOptions()).playBlankMp3();
        return adPrime ?? enginePrime;
    }
    async requestAdBreak(provider, adRequestId, adRequestUrl) {
        const { resolve, reject, promise } = Promise.withResolvers();
        const AdProvider = adProviderMap[provider];
        this.#destroyAdsProvider();
        this.#loadId = uuidv4();
        const loadId = this.#loadId;
        try {
            await AdProvider.ready;
        }
        catch (error) {
            if (loadId !== this.#loadId) {
                throw new Error('Ad request cancelled by new load request');
            }
            debug('Ad SDK failed to load', error);
            throw new PlayerError(error?.message || 'Ad SDK failed to load', ErrorCode.AdSDKLoadError, ErrorType.LOAD_ERROR, ErrorName.AdError, false);
        }
        if (loadId !== this.#loadId) {
            throw new Error('Ad request cancelled by new load request');
        }
        const onAllAdsComplete = (event) => {
            debug('Ad break exhaustion observed', {
                eventType: event?.eventType,
                totalAds: event?.totalAds || 0,
                adsStartedCount,
            });
            this.#state = PlayerState.IDLE;
            this.#destroyAdsProvider();
            resolve(adsStartedCount);
        };
        let didAdLoadFail = false;
        const onAdPlaybackError = (event) => {
            didAdLoadFail = true;
            this.#state = PlayerState.IDLE;
            this.#destroyAdsProvider();
            reject(event);
        };
        let adsStartedCount = 0;
        const onAdStart = (event) => {
            this.off('aderror', onAdPlaybackError);
            adsStartedCount++;
            this.emit('companionAds', {
                companionAds: event.companionAds || [],
                requestId: adRequestId,
                provider: provider,
            });
        };
        const onStopped = () => {
            resolve(adsStartedCount);
        };
        try {
            debug(`Setting ads provider to ${provider}`);
            this.#ads = new AdProvider(this.#options.advertising?.providerOptions);
            this.#registerAdProviderEvents();
            this.on('aderror', onAdPlaybackError);
            debug('Loading ads');
            await this.#ads.load(adRequestId, adRequestUrl);
            if (loadId !== this.#loadId) {
                throw new Error('Ad request cancelled by new load request');
            }
            if (didAdLoadFail) {
                return 0;
            }
            debug('Ads loaded');
            this.#destroyEngine();
            this.#isAdBreak = true;
            this.on('adstart', onAdStart);
            this.once('alladscomplete', onAllAdsComplete);
            this.once('stopped', onStopped);
            await this.play();
            if (didAdLoadFail) {
                return promise;
            }
            this.#state = PlayerState.PLAYING;
            return promise;
        }
        catch (e) {
            this.#destroyAdsProvider();
            throw e;
        }
        finally {
            promise.catch(noop).finally(() => {
                this.off('adstart', onAdStart);
                this.off('alladscomplete', onAllAdsComplete);
                this.off('aderror', onAdPlaybackError);
                this.off('stopped', onStopped);
            });
        }
    }
    async load(src) {
        debug('load', { state: this.#state });
        this.#wasPlaybackAttempted = false;
        this.#wasPlaybackStarted = false;
        this.#isUserInitiatedPause = false;
        this.#loadId = uuidv4();
        const loadId = this.#loadId;
        this.#cancelRetry?.();
        this.#state = PlayerState.LOADING;
        try {
            if (src) {
                this.#setSources(src);
            }
            await this.#processCurrentSource();
            if (loadId !== this.#loadId) {
                return;
            }
            this.#setupEngine();
            try {
                await rejectAfter(this.#engine?.load(), this.#options.loadTimeoutMs);
                if (loadId !== this.#loadId) {
                    return;
                }
                if (this.isLive()) {
                    this.#cachedPlaybackRate = null;
                }
                this.#state = PlayerState.LOADED;
            }
            catch (error) {
                if (loadId !== this.#loadId) {
                    return;
                }
                if (error === 'TIMED_OUT') {
                    this.#onError(PlayerError.createError(ErrorName.RequestTimeout, `load() timed out after ${this.#options.loadTimeoutMs}ms`));
                    await this.#playNextSource();
                    return;
                }
                if (this.getEngineName() === IcyEngine.name) {
                    this.#retryOnceWithCoreEngine = true;
                    await this.load();
                    return;
                }
                await this.#playNextSource();
            }
            if (this.#options.autoPlay && loadId === this.#loadId) {
                debug('Attempting to autoplay...');
                await this.play();
            }
        }
        catch (error) {
            this.#onError(PlayerError.createError(error instanceof PlayerError ? error : ErrorName.LoadError, error.message));
            return this.#playNextSource();
        }
    }
    async play() {
        debug('play', { state: this.#state });
        const loadId = this.#loadId;
        this.#wasPlaybackAttempted = true;
        this.#isUserInitiatedPause = false;
        if (this.isAdBreak()) {
            this.#ads?.setVolume(this.#volume);
            this.#ads?.play();
            return;
        }
        if (this.isFailed() || this.isPlaying() || this.isLoading()) {
            return;
        }
        if (this.isPaused()) {
            this.#retryOnceOnError = true;
        }
        this.#engine?.setVolume(this.#volume);
        try {
            await rejectAfter(this.#engine?.play(), this.#options.loadTimeoutMs);
            this.#wasPlaybackStarted = true;
        }
        catch (error) {
            if (loadId !== this.#loadId) {
                return;
            }
            if (error === 'TIMED_OUT') {
                this.#onError(PlayerError.createError(ErrorName.RequestTimeout, `play() timed out after ${this.#options.loadTimeoutMs}ms`));
            }
            return this.#playNextSource();
        }
    }
    #destroyEngine() {
        this.#previousEngineName = this.#engine?.name;
        this.#deregisterEngineEvents();
        this.#engine?.destroy();
        if (!this.#options.onlyUseCoreEngine) {
            this.#engine = undefined;
        }
    }
    stop() {
        debug('stop', { state: this.#state });
        this.#state = PlayerState.IDLE;
        this.#loadId = null;
        this.#isUserInitiatedPause = false;
        this.#cancelRetry?.();
        this.#destroyAdsProvider();
        this.#destroyEngine();
        this.emit('stopped');
    }
    pause() {
        this.#cancelRetry?.();
        if (this.isPlaying()) {
            this.#isUserInitiatedPause = true;
            if (this.isAdBreak()) {
                this.#ads?.pause?.();
            }
            else {
                this.#engine?.pause();
            }
        }
    }
    mute(value) {
        if (this.muted === value) {
            return;
        }
        if (this.#ads) {
            this.#ads.setVolume(value ? 0 : this.#volume);
            this.#onVolumeChange();
        }
        else if (this.#engine) {
            this.#engine.mute(value);
            this.#onVolumeChange();
        }
    }
    seekTo(seekTime) {
        if (!this.isAdBreak()) {
            this.#engine?.seekTo(seekTime);
        }
    }
    seekBackward(seekOffset) {
        this.#engine?.seekBackward(seekOffset);
    }
    seekForward(seekOffset) {
        this.#engine?.seekForward(seekOffset);
    }
    setSSAIManager(ssaiManager) {
        if (this.#ssaiManager) {
            this.#ssaiManager.off('error', this.#onSsaiError);
            this.#ssaiManager.off('info', this.#onSsaiInfo);
        }
        this.#ssaiManager = ssaiManager;
        this.#ssaiManager.on('error', this.#onSsaiError);
        this.#ssaiManager.on('info', this.#onSsaiInfo);
    }
    setVolume(value) {
        this.#volume = clamp(value, 0, 1);
        if (this.#isAdBreak && this.#ads) {
            this.#ads.setVolume?.(this.#volume);
        }
        else if (this.#engine) {
            this.#engine.setVolume(this.#volume);
        }
        else {
            this.#onVolumeChange();
        }
        this.#settingsStorage?.set({ volume: this.#volume });
    }
    getPlaybackRate() {
        return this.#engine?.getPlaybackRate();
    }
    setPlaybackRate(rate) {
        this.#engine?.setPlaybackRate(rate);
        this.#cachedPlaybackRate = rate === 1 ? null : rate;
    }
    setInstreamAdParams(adParams) {
        this.#ssaiManager?.setRequestParams(adParams);
    }
    isLoading() {
        return this.#state === PlayerState.LOADING;
    }
    isLoaded() {
        return this.#state === PlayerState.LOADED;
    }
    isPlaying() {
        return this.#state === PlayerState.PLAYING;
    }
    isPaused() {
        return this.#state === PlayerState.PAUSED;
    }
    isIdle() {
        return this.#state === PlayerState.IDLE;
    }
    isFailed() {
        return this.#state === PlayerState.FAILED;
    }
    isAdBreak() {
        return this.#isAdBreak;
    }
    isLive() {
        return this.isAdBreak() ? false : this.#engine?.isLive();
    }
    getDuration() {
        if (this.isAdBreak()) {
            return this.#ads?.duration || 0;
        }
        return this.#engine?.getDuration() || 0;
    }
    getCurrentTime() {
        if (this.isAdBreak()) {
            return this.#ads?.currentTime || 0;
        }
        return this.#engine?.getCurrentTime() || 0;
    }
    getVolume() {
        if (this.muted) {
            return 0;
        }
        if (this.isAdBreak() && this.#ads) {
            return this.#ads.volume;
        }
        if (this.#engine) {
            return this.#engine.getVolume();
        }
        return this.#volume;
    }
    getTextTracks() {
        return this.#engine?.getTextTracks();
    }
    getMediaElement() {
        const mediaElement = this.#engine?.getElement();
        if (mediaElement) {
            return mediaElement;
        }
        const mediaElementId = this.#options.mediaElementId;
        if (!mediaElementId) {
            return undefined;
        }
        return (globalThis.document?.getElementById(mediaElementId) ?? undefined);
    }
    resizeVideoAd(width, height) {
        this.#ads?.resizeVideo?.(width, height);
    }
    forceSkipAd() {
        this.#ads?.forceSkipAd();
    }
    #buildEventMaps() {
        this.#engineEvents = new Map([
            ['abort', this.#onAbort],
            ['canplay', this.#onCanPlay],
            ['canplaythrough', this.#onCanPlayThrough],
            ['durationchange', this.#onDurationChange],
            ['emptied', this.#onEmptied],
            ['ended', this.#onEnded],
            ['error', this.#onError],
            ['loadedmetadata', this.#onLoadedMetadata],
            ['loadeddata', this.#onLoadedData],
            ['loadstart', this.#onLoadStart],
            ['meta', this.#onMeta],
            ['metadata', this.#onMetadata],
            ['pause', this.#onPause],
            ['play', this.#onPlay],
            ['playing', this.#onPlaying],
            ['progress', this.#onProgress],
            ['ratechange', this.#onRateChange],
            ['seeked', this.#onSeeked],
            ['seeking', this.#onSeeking],
            ['stalled', this.#onStalled],
            ['suspend', this.#onSuspend],
            ['texttrackcuechange', this.#onTextTrackCueChange],
            ['timeupdate', this.#onTimeUpdate],
            ['volumechange', this.#onVolumeChange],
            ['unlock', this.#onUnlock],
            ['waiting', this.#onWaiting],
        ]);
        this.#adProviderEvents = new Map([
            ['adbuffering', this.#onAdEvent],
            ['adclick', this.#onAdEvent],
            ['adcomplete', this.#onAdEvent],
            ['addurationchange', this.#onAdEvent],
            ['aderror', this.#onAdError],
            ['adimpression', this.#onAdEvent],
            ['adinteraction', this.#onAdEvent],
            ['adloaded', this.#onAdEvent],
            ['admetadata', this.#onAdEvent],
            ['adpause', this.#onAdPause],
            ['adprogress', this.#onAdEvent],
            ['adresume', this.#onAdResume],
            ['adstart', this.#onAdStart],
            ['adskipped', this.#onAdSkipped],
            ['advolumechange', this.#onAdEvent],
            ['advolumemuted', this.#onAdEvent],
            ['contentresumerequested', this.#onContentResumeRequested],
            ['contentpauserequested', this.#onContentPauseRequested],
            ['alladscomplete', this.#onAllAdsComplete],
            ['adplaybackcancelled', this.#onAdPlaybackCancelled],
        ]);
    }
    #initAdsProvider() {
        const { providers, privacyData } = this.#options.advertising || {};
        if (providers?.includes('google')) {
            GoogleIMAProvider.init();
        }
        if (providers?.includes('adswizz')) {
            AdsWizzProvider.init(privacyData);
        }
    }
    async #retrySource() {
        debug('Retrying current source...');
        this.#isRetrying = true;
        if (!this.#options.loadTimeoutMs) {
            this.#retryOnceOnError = true;
        }
        const cancelRetryAbortController = new AbortController();
        const timeoutAbortController = new AbortController();
        const timeoutMs = this.#options.loadTimeoutMs || DEFAULT_TIMEOUT_MS;
        const retries = this.#retryOnceOnError ? 0 : timeoutMs / 1000 - 1;
        debug('Max retries:', retries + 1);
        if (!this.isLive() && this.getCurrentTime()) {
            this.currentSource.position = this.getCurrentTime();
        }
        this.#engine?.setOptions(this.#buildEngineOptions());
        setTimeout(() => {
            try {
                timeoutAbortController.abort();
            }
            catch {
            }
        }, timeoutMs);
        this.#cancelRetry = () => {
            try {
                cancelRetryAbortController.abort();
            }
            catch {
            }
        };
        try {
            await asyncRetry(async (bail, attempt) => {
                if (cancelRetryAbortController.signal.aborted) {
                    return;
                }
                if (timeoutAbortController.signal.aborted) {
                    bail(new Error('Bailing'));
                    return;
                }
                debug('Retry attempt:', attempt);
                this.emit('retrying', { attempt });
                try {
                    await rejectAfter(this.#engine?.load(), this.#options.loadTimeoutMs);
                }
                catch {
                    throw new Error('Failed to load');
                }
                if (cancelRetryAbortController.signal.aborted) {
                    return;
                }
                if (timeoutAbortController.signal.aborted) {
                    bail(new Error('Bailing'));
                    return;
                }
                try {
                    await rejectAfter(this.#engine?.play(), this.#options.loadTimeoutMs);
                    this.#isRetrying = false;
                }
                catch {
                    throw new Error('Failed to play');
                }
            }, {
                retries,
                factor: 1,
                maxTimeout: 3000,
            });
        }
        catch {
            this.#isRetrying = false;
            if (!this.#retryOnceOnError) {
                this.#onError(PlayerError.createError(ErrorName.RequestTimeout, `retry timed out after ${timeoutMs}ms`));
            }
        }
        finally {
            this.#retryOnceOnError = false;
        }
    }
    async #playNextSource() {
        if (this.isFailed() || !this.#src[this.#currentSourceIndex + 1]) {
            return;
        }
        debug('Trying next source...');
        this.#currentSourceIndex++;
        const wasPlaybackAttempted = this.#wasPlaybackAttempted;
        await this.load();
        if (wasPlaybackAttempted && this.isLoaded()) {
            await this.play();
        }
    }
    async #setSources(src) {
        this.#currentSourceIndex = 0;
        if (!src) {
            this.#src = [];
            return;
        }
        if (typeof src === 'string') {
            this.#src = [{ url: src }];
            return;
        }
        if (Array.isArray(src)) {
            this.#src = src.map((source) => (typeof source === 'string' ? { url: source } : source));
            return;
        }
        if (isPlainObject(src)) {
            this.#src = [src];
            return;
        }
        this.#src = [];
    }
    async #maybeParsePlaylist() {
        const { url, format, playlistHandler: customPlaylistHandler } = this.currentSource;
        const { pathname } = new URL(url);
        const canParsePlaylist = format === 'pls' || pathname.endsWith('.pls') || customPlaylistHandler;
        if (!canParsePlaylist) {
            return;
        }
        try {
            let playlist;
            if (customPlaylistHandler) {
                const customPlaylist = await customPlaylistHandler(this.currentSource);
                playlist = Array.isArray(customPlaylist) ? customPlaylist : [customPlaylist];
            }
            else {
                const rawPlaylist = await fetchWithRetry(url, { parseAs: 'text' });
                if (rawPlaylist) {
                    playlist = parsePlaylist(rawPlaylist)?.map((url) => ({ url }));
                }
            }
            if (!playlist?.length) {
                throw new Error('Empty playlist');
            }
            this.#src.splice(this.#currentSourceIndex, 1, ...playlist);
        }
        catch (error) {
            throw PlayerError.createError(ErrorName.PlaylistParseError, error?.message);
        }
    }
    async #maybeResolveSSAI() {
        const { isSSAI, url } = this.currentSource;
        if (isSSAI) {
            await this.#ssaiManager?.requestStream(url);
            this.currentSource.url = this.#ssaiManager?.getStreamUrl() ?? url;
        }
    }
    async #processCurrentSource() {
        if (!this.#src.length) {
            throw PlayerError.createError(ErrorName.MissingContentSource);
        }
        if (!this.currentSource) {
            throw new Error('Invalid source index');
        }
        if (!this.currentSource?.url) {
            throw new Error('Missing url');
        }
        await this.#maybeParsePlaylist();
        await this.#maybeResolveSSAI();
    }
    #buildEngineOptions() {
        const { url, position } = this.currentSource || {};
        return {
            src: url,
            startPosition: position,
            playbackRate: this.#cachedPlaybackRate,
            ...pick(this.#options, ['mediaElementId', 'autoPlay', 'debug', 'hlsLiveSeekFragTagKey']),
        };
    }
    #selectEngine() {
        if (this.#options.onlyUseCoreEngine) {
            const engine = this.#engine || new BaseEngine();
            engine.setOptions(this.#buildEngineOptions());
            return engine;
        }
        const { enableIcyMetadata } = this.#options;
        const { format, isLiveSeekStream, useStreamMetadata } = this.currentSource;
        const engineOptions = this.#buildEngineOptions();
        if (this.#retryOnceWithCoreEngine) {
            debug('Set engine to HTML5');
            this.#retryOnceWithCoreEngine = false;
            return new BaseEngine(engineOptions);
        }
        if (format === 'hls') {
            debug('Set engine to HLS');
            return isLiveSeekStream && engineOptions.hlsLiveSeekFragTagKey
                ? new HLSLiveSeekEngine(engineOptions)
                : new HLSEngine(engineOptions);
        }
        if (enableIcyMetadata && useStreamMetadata && IcyEngine.browserSupportsFormat(format)) {
            debug('Set engine to Icy');
            return new IcyEngine(engineOptions);
        }
        debug('Set engine to HTML5');
        return new BaseEngine(engineOptions);
    }
    #setupEngine() {
        this.#destroyAdsProvider();
        this.#destroyEngine();
        this.#engine = this.#selectEngine();
        if (!this.isAdBreak()) {
            this.#registerEngineEvents();
        }
        this.#engine?.setup();
        this.#updateSsaiBeacons();
    }
    #setSettingsFromStorage() {
        const settings = this.#settingsStorage?.get();
        if (!settings) {
            return;
        }
        if (settings.volume !== undefined) {
            this.setVolume(settings.volume);
        }
    }
    #destroyAdsProvider() {
        this.#isAdBreak = false;
        this.#deregisterAdProviderEvents();
        this.#ads?.destroy();
        this.#ads = null;
    }
    #registerAdProviderEvents = () => {
        this.#adProviderEvents.forEach((cb, name) => {
            this.#ads?.off(name, cb);
            this.#ads?.on(name, cb);
        });
    };
    #deregisterAdProviderEvents = () => {
        this.#adProviderEvents.forEach((cb, name) => {
            this.#ads?.off(name, cb);
        });
    };
    #registerEngineEvents = () => {
        this.#engineEvents.forEach((cb, name) => {
            this.#engine?.off(name, cb);
            this.#engine?.on(name, cb);
        });
    };
    #deregisterEngineEvents = () => {
        this.#engineEvents.forEach((cb, name) => {
            this.#engine?.off(name, cb);
        });
    };
    #updateSsaiBeacons = () => {
        const el = this.#engine?.getElement();
        if (el) {
            this.#ssaiManager?.updateBeaconListeners(el);
        }
    };
    #onSsaiError = (error) => {
        this.#onError(error);
    };
    #onSsaiInfo = (info) => {
        this.emit('info', info);
    };
    #onAbort = () => {
        this.emit('abort');
    };
    #onCanPlay = () => {
        this.#emitWithSource('canplay');
    };
    #onCanPlayThrough = () => {
        this.emit('canplaythrough');
    };
    #onDurationChange = () => {
        this.emit('durationchange');
    };
    #onEmptied = () => {
        if (!this.isLoading()) {
            debug('emptied', { state: this.#state });
            this.emit('emptied');
        }
    };
    #onEnded = () => {
        if (this.isLive() && !this.#isRetrying) {
            this.#retrySource();
            return;
        }
        debug('ended', { previousState: this.#state });
        this.#state = PlayerState.IDLE;
        this.emit('ended');
    };
    #onLoadedMetadata = () => {
        this.emit('loadedmetadata');
    };
    #onLoadedData = () => {
        this.emit('loadeddata');
    };
    #onLoadStart = () => {
        debug('loadstart', { state: this.#state });
        this.emit('loadstart');
    };
    #onMeta = (event) => {
        this.emit('meta', event);
        this.#ssaiManager?.processMetadata(event);
    };
    #onMetadata = (metadata) => {
        this.emit('metadata', metadata);
    };
    #onPause = () => {
        debug('pause', { previousState: this.#state });
        if (this.#state !== PlayerState.IDLE) {
            this.#state = PlayerState.PAUSED;
        }
        this.emit('pause');
    };
    #onPlay = () => {
        this.emit('play');
    };
    #onPlaying = () => {
        debug('playing', { previousState: this.#state });
        this.#state = PlayerState.PLAYING;
        this.#emitWithSource('playing');
    };
    #onProgress = () => {
        debug('progress');
        this.emit('progress');
    };
    #emitWithSource = (eventType, error) => {
        const index = this.#currentSourceIndex;
        const eventData = {
            index,
            source: this.#src[index] || {},
            isLastSource: index === this.#src.length - 1,
            ...(error && { error }),
        };
        this.emit(eventType, eventData);
    };
    #onError = async (error) => {
        const playerError = PlayerError.createError(error);
        debug('error', playerError);
        if (!playerError.fatal) {
            this.#emitWithSource('error', playerError);
            return;
        }
        if (this.#isRetrying && !this.#retryOnceOnError) {
            return;
        }
        if ((!this.#isRetrying && this.#retryOnceOnError) ||
            playerError.name === ErrorName.PlaybackErrorNetwork) {
            this.#retrySource();
            return;
        }
        this.#destroyEngine();
        if (this.#src[this.#currentSourceIndex + 1]) {
            this.#emitWithSource('error', playerError);
            if (this.#wasPlaybackStarted) {
                this.#playNextSource();
            }
            return;
        }
        this.#state = PlayerState.FAILED;
        this.#emitWithSource('error', playerError);
    };
    #onRateChange = () => {
        this.emit('ratechange');
    };
    #onSeeked = async () => {
        this.emit('seeked');
    };
    #onSeeking = () => {
        this.emit('seeking');
    };
    #onStalled = async () => {
        debug('stalled', { state: this.#state });
        this.emit('stalled');
        const isPlayerStateEligibleForRetry = () => this.isPlaying() || (this.isPaused() && !this.#isUserInitiatedPause);
        const isEligibleForRetry = isPlayerStateEligibleForRetry() &&
            !this.#isRetrying &&
            this.getEngineName() !== HLSEngine.name;
        if (!isEligibleForRetry) {
            return;
        }
        const { resolve, promise } = Promise.withResolvers();
        const loadId = this.#loadId;
        let timeUpdateTimeoutId;
        let lastTimeUpdateTimestamp = Date.now();
        let hasRecovered = false;
        const done = () => {
            this.#engine?.off('progress', onRecovered);
            this.#engine?.off('waiting', onRecovered);
            this.#engine?.off('timeupdate', onTimeUpdate);
            clearTimeout(timeUpdateTimeoutId);
            resolve();
        };
        const onTimeUpdate = () => {
            lastTimeUpdateTimestamp = Date.now();
        };
        const onRecovered = () => {
            hasRecovered = true;
            done();
        };
        const startTimeout = (timeout = WAITING_RECOVERY_MS) => {
            timeUpdateTimeoutId = setTimeout(() => {
                const timeSinceLastTimeUpdate = Date.now() - lastTimeUpdateTimestamp;
                if (timeSinceLastTimeUpdate >= WAITING_RECOVERY_MS) {
                    done();
                    return;
                }
                const nextTimeout = WAITING_RECOVERY_MS - timeSinceLastTimeUpdate;
                startTimeout(Math.ceil(nextTimeout / 1000) * 1000);
            }, timeout);
        };
        startTimeout();
        this.#engine?.once('progress', onRecovered);
        this.#engine?.once('waiting', onRecovered);
        this.#engine?.on('timeupdate', onTimeUpdate);
        await promise;
        if (isPlayerStateEligibleForRetry() &&
            !this.#isRetrying &&
            !hasRecovered &&
            loadId === this.#loadId) {
            this.#retrySource();
        }
    };
    #onSuspend = () => {
        this.emit('suspend');
    };
    #onTextTrackCueChange = (event) => {
        this.emit('texttrackcuechange', event);
    };
    #onTimeUpdate = () => {
        debug('timeupdate');
        this.emit('timeupdate');
    };
    #onVolumeChange = () => {
        this.emit('volumechange');
    };
    #onWaiting = async () => {
        debug('waiting');
        this.emit('waiting');
        if (this.isPlaying() && !this.#isRetrying) {
            const { resolve, promise } = Promise.withResolvers();
            const loadId = this.#loadId;
            this.#engine?.once('playing', resolve);
            const didResumePlaying = await resolveAfter(promise, WAITING_RECOVERY_MS);
            if (this.isPlaying() &&
                !this.#isRetrying &&
                !didResumePlaying &&
                loadId === this.#loadId) {
                this.#retrySource();
            }
        }
    };
    #onUnlock = async () => {
        debug('Autoplay functionality unlocked');
        if (this.#options.autoPlay) {
            debug('Attempting to autoplay...');
            await this.play();
        }
        this.emit('unlock');
    };
    #onContentResumeRequested = async (event) => {
        this.emit('contentresumerequested', event);
    };
    #onContentPauseRequested = async () => {
        this.emit('contentpauserequested');
    };
    #onAdEvent = (event) => {
        debug(event?.eventType);
        this.emit(event?.eventType, event);
    };
    #onAllAdsComplete = (event) => {
        this.emit('alladscomplete', event);
    };
    #onAdPlaybackCancelled = () => {
        this.emit('adplaybackcancelled');
    };
    #onAdSkipped = (event) => {
        this.emit('adskipped', event);
    };
    #onAdError = async (errorData) => {
        debug('Ad error', errorData);
        this.emit('aderror', errorData);
    };
    #onAdPause = (event) => {
        this.#state = PlayerState.PAUSED;
        this.emit('adpause', event);
    };
    #onAdResume = (event) => {
        this.#state = PlayerState.PLAYING;
        this.emit('adresume', event);
    };
    #onAdStart = (event) => {
        this.#state = PlayerState.PLAYING;
        this.emit('adstart', event);
    };
}
